// IMF API Service for Silk Road Terminal
// Fetches economic forecasts from IMF World Economic Outlook

import { cacheService, DEFAULT_TTL } from '../data/cacheService';
import { IMF_INDICATOR_CODES, getIMFCountryCode, type APIResponse } from './types';

const IMF_BASE_URL = 'https://www.imf.org/external/datamapper/api/v1';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

export interface IMFDataPoint {
  year: number;
  value: number | null;
  isForecast?: boolean;
}

export interface IMFForecast {
  indicatorId: string;
  countryCode: string;
  baseYear: number;
  historical: IMFDataPoint[];
  forecast: IMFDataPoint[];
  source: string;
  lastUpdated: Date;
}

// Sleep helper for retry delays
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

// Parse IMF API response
const parseIMFResponse = (
  data: { values?: Record<string, Record<string, number>> },
  countryCode: string,
  indicatorCode: string
): IMFDataPoint[] => {
  if (!data?.values?.[indicatorCode]?.[countryCode]) {
    return [];
  }

  const values = data.values[indicatorCode][countryCode];
  const currentYear = new Date().getFullYear();

  return Object.entries(values)
    .map(([year, value]) => ({
      year: parseInt(year, 10),
      value: value !== null ? Number(value) : null,
      isForecast: parseInt(year, 10) > currentYear,
    }))
    .filter(d => !isNaN(d.year))
    .sort((a, b) => a.year - b.year);
};

// Fetch with retry logic
const fetchWithRetry = async (
  url: string,
  retries: number = MAX_RETRIES
): Promise<unknown> => {
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.warn(`IMF API attempt ${attempt}/${retries} failed:`, lastError.message);

      if (attempt < retries) {
        await sleep(RETRY_DELAY * attempt);
      }
    }
  }

  throw lastError || new Error('Failed to fetch from IMF API');
};

/**
 * Fetch indicator data from IMF (includes forecasts)
 */
export const fetchIndicator = async (
  countryCode: string,
  indicatorId: string,
  startYear?: number,
  endYear?: number
): Promise<APIResponse<IMFDataPoint[]>> => {
  const imfCountry = getIMFCountryCode(countryCode);
  const imfIndicator = IMF_INDICATOR_CODES[indicatorId];

  if (!imfIndicator) {
    return {
      data: null,
      error: `Unknown IMF indicator: ${indicatorId}`,
      source: 'live',
      lastUpdated: null,
      isStale: true,
    };
  }

  const cacheKey = `imf_indicator_${imfCountry}_${imfIndicator}`;

  // Check cache
  const cached = cacheService.getStale<IMFDataPoint[]>(cacheKey);
  if (cached.data && !cached.isStale) {
    let data = cached.data;
    if (startYear || endYear) {
      data = data.filter(d =>
        (!startYear || d.year >= startYear) &&
        (!endYear || d.year <= endYear)
      );
    }
    return {
      data,
      error: null,
      source: 'cached',
      lastUpdated: new Date(),
      isStale: false,
    };
  }

  try {
    const url = `${IMF_BASE_URL}/${imfIndicator}/${imfCountry}`;
    const response = await fetchWithRetry(url) as { values?: Record<string, Record<string, number>> };
    const data = parseIMFResponse(response, imfCountry, imfIndicator);

    cacheService.set(cacheKey, data, DEFAULT_TTL);

    let filteredData = data;
    if (startYear || endYear) {
      filteredData = data.filter(d =>
        (!startYear || d.year >= startYear) &&
        (!endYear || d.year <= endYear)
      );
    }

    return {
      data: filteredData,
      error: null,
      source: 'live',
      lastUpdated: new Date(),
      isStale: false,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    if (cached.data) {
      let data = cached.data;
      if (startYear || endYear) {
        data = data.filter(d =>
          (!startYear || d.year >= startYear) &&
          (!endYear || d.year <= endYear)
        );
      }
      return {
        data,
        error: errorMessage,
        source: 'cached',
        lastUpdated: new Date(),
        isStale: true,
      };
    }

    return {
      data: null,
      error: errorMessage,
      source: 'live',
      lastUpdated: null,
      isStale: true,
    };
  }
};

/**
 * Fetch forecasts for an indicator
 */
export const fetchForecasts = async (
  countryCode: string,
  indicatorId: string,
  yearsAhead: number = 5
): Promise<APIResponse<IMFForecast>> => {
  const response = await fetchIndicator(countryCode, indicatorId);

  if (!response.data) {
    return {
      data: null,
      error: response.error,
      source: response.source,
      lastUpdated: response.lastUpdated,
      isStale: response.isStale,
    };
  }

  const currentYear = new Date().getFullYear();
  const historical = response.data.filter(d => d.year <= currentYear);
  const forecast = response.data.filter(d => d.year > currentYear && d.year <= currentYear + yearsAhead);

  const forecastData: IMFForecast = {
    indicatorId,
    countryCode,
    baseYear: currentYear,
    historical,
    forecast,
    source: 'IMF World Economic Outlook',
    lastUpdated: new Date(),
  };

  return {
    data: forecastData,
    error: null,
    source: response.source,
    lastUpdated: new Date(),
    isStale: response.isStale,
  };
};

/**
 * Fetch indicator for multiple countries (peer comparison)
 */
export const fetchPeerComparison = async (
  countryCodes: string[],
  indicatorId: string,
  year?: number
): Promise<APIResponse<Record<string, number | null>>> => {
  const targetYear = year || new Date().getFullYear();
  const imfIndicator = IMF_INDICATOR_CODES[indicatorId];

  if (!imfIndicator) {
    return {
      data: null,
      error: `Unknown IMF indicator: ${indicatorId}`,
      source: 'live',
      lastUpdated: null,
      isStale: true,
    };
  }

  const cacheKey = `imf_peer_${countryCodes.join('_')}_${imfIndicator}_${targetYear}`;

  // Check cache
  const cached = cacheService.getStale<Record<string, number | null>>(cacheKey);
  if (cached.data && !cached.isStale) {
    return {
      data: cached.data,
      error: null,
      source: 'cached',
      lastUpdated: new Date(),
      isStale: false,
    };
  }

  try {
    const imfCountries = countryCodes.map(getIMFCountryCode).join('+');
    const url = `${IMF_BASE_URL}/${imfIndicator}/${imfCountries}`;
    // IMF response structure: { values: { [indicator]: { [country]: { [year]: value } } } }
    const response = await fetchWithRetry(url) as {
      values?: Record<string, Record<string, Record<string, number>>>
    };

    const result: Record<string, number | null> = {};

    for (const code of countryCodes) {
      const imfCode = getIMFCountryCode(code);
      const countryData = response?.values?.[imfIndicator]?.[imfCode];

      if (countryData && typeof countryData === 'object') {
        // Get the value for target year, or most recent if not available
        const yearStr = String(targetYear);
        if (countryData[yearStr] !== undefined) {
          result[code] = countryData[yearStr];
        } else {
          // Find most recent year with data
          const years = Object.keys(countryData).map(Number).filter(y => y <= targetYear).sort((a, b) => b - a);
          result[code] = years.length > 0 ? countryData[String(years[0])] : null;
        }
      } else {
        result[code] = null;
      }
    }

    cacheService.set(cacheKey, result, DEFAULT_TTL);

    return {
      data: result,
      error: null,
      source: 'live',
      lastUpdated: new Date(),
      isStale: false,
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';

    if (cached.data) {
      return {
        data: cached.data,
        error: errorMessage,
        source: 'cached',
        lastUpdated: new Date(),
        isStale: true,
      };
    }

    return {
      data: null,
      error: errorMessage,
      source: 'live',
      lastUpdated: null,
      isStale: true,
    };
  }
};

/**
 * Check if IMF API is accessible
 */
export const checkAPIHealth = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${IMF_BASE_URL}/NGDP_RPCH/USA`, { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
};

/**
 * Get list of available IMF indicators
 */
export const getAvailableIndicators = (): string[] => {
  return Object.keys(IMF_INDICATOR_CODES);
};

/**
 * Get IMF indicator code from ID
 */
export const getIndicatorCode = (indicatorId: string): string | undefined => {
  return IMF_INDICATOR_CODES[indicatorId];
};

// Export default object
const imfApi = {
  fetchIndicator,
  fetchForecasts,
  fetchPeerComparison,
  checkAPIHealth,
  getAvailableIndicators,
  getIndicatorCode,
  IMF_INDICATOR_CODES,
};

export default imfApi;
